import { useState } from "react";
import { useCustom, useCustomMutation, useTranslate } from "@refinedev/core";
import { Button, CheckIcon, ClockIcon, Modal, PlusIcon, StatusBadge, XIcon } from "@fawkes/ui";

// ── Mijn teams (Maarten, 2026-10-09) ────────────────────────────────────────────────────────────────
//
// Iemand heeft vaste teams (door beheer gezet) en kan zichzelf voor vandaag aan een team toevoegen: "vandaag sta ik
// bij de katten". Dat telt mee als "mijn team" voor de takenlijst en vervalt om middernacht. Op het dashboard staan
// je teams als chips onder de begroeting; "Teams beheren" opent de kaart.

interface Team { code: string; naam: string; actief: boolean }
interface MijnOverzicht { vast: Team[]; vandaag: Team[]; kiesbaar: Team[] }

const URL = "taak-teams/mijn/overzicht";

/** Een dier bij een teamnaam ("Dierverzorging katten" → 🐱), anders niets: de chip is dan alleen de naam. */
export function teamIcoon(naam: string): string | null {
  const n = naam.toLowerCase();
  if (/kat(ten)?\b|poes/.test(n)) return "🐱";
  if (/hond/.test(n)) return "🐾";
  if (/konijn|knaag|kleine dieren/.test(n)) return "🐰";
  if (/vogel/.test(n)) return "🐦";
  return null;
}

/** De chips onder de begroeting op het dashboard, met de link naar "Mijn teams". */
export function MijnTeamsChips() {
  const t = useTranslate();
  const { data, refetch } = useCustom<MijnOverzicht>({ url: URL, method: "get" });
  const [open, setOpen] = useState(false);
  const o = data?.data;
  const vast = Array.isArray(o?.vast) ? o!.vast : [];
  const vandaag = Array.isArray(o?.vandaag) ? o!.vandaag : [];
  if (!o) return null;
  return (
    <div className="mt-chips">
      {vast.map((x) => (
        <span key={x.code} className="mt-chip">
          {teamIcoon(x.naam) && <span aria-hidden="true">{teamIcoon(x.naam)}</span>}
          {x.naam}
        </span>
      ))}
      {vandaag.map((x) => (
        <span key={x.code} className="mt-chip mt-chip--vandaag">
          {teamIcoon(x.naam) && <span aria-hidden="true">{teamIcoon(x.naam)}</span>}
          {x.naam} · {t("mijnTeams.vandaagKort")}
        </span>
      ))}
      <button type="button" className="mt-link" onClick={() => setOpen(true)}>
        {t(vast.length + vandaag.length === 0 ? "mijnTeams.kiezen" : "mijnTeams.beheren")} →
      </button>
      {open && <MijnTeamsDialoog onSluit={() => { setOpen(false); void refetch(); }} />}
    </div>
  );
}

/** De kaart "Mijn teams" in een venster. */
export function MijnTeamsDialoog({ onSluit }: { onSluit: () => void }) {
  const t = useTranslate();
  return (
    <Modal open title={t("mijnTeams.titel")} onClose={onSluit}
      footer={<Button variant="secondary" onClick={onSluit}>{t("common.close")}</Button>}>
      <MijnTeamsKaart />
    </Modal>
  );
}

export function MijnTeamsKaart() {
  const t = useTranslate();
  const { data, refetch } = useCustom<MijnOverzicht>({ url: URL, method: "get" });
  const { mutate, isLoading: bezig } = useCustomMutation();
  const [keuze, setKeuze] = useState("");
  const [fout, setFout] = useState<string | null>(null);
  const o = data?.data;
  const vast = Array.isArray(o?.vast) ? o!.vast : [];
  const vandaag = Array.isArray(o?.vandaag) ? o!.vandaag : [];
  const kiesbaar = Array.isArray(o?.kiesbaar) ? o!.kiesbaar : [];
  const gekozen = keuze && kiesbaar.some((x) => x.code === keuze) ? keuze : kiesbaar[0]?.code ?? "";

  const klaar = { onSuccess: () => { setFout(null); void refetch(); }, onError: () => setFout(t("mijnTeams.mislukt")) };
  const voegToe = () => {
    if (!gekozen) return;
    mutate({ url: "taak-teams/mijn/vandaag", method: "post", values: { team: gekozen },
      successNotification: false, errorNotification: false }, klaar);
  };
  const haalWeg = (code: string) =>
    mutate({ url: `taak-teams/mijn/vandaag/${encodeURIComponent(code)}`, method: "delete", values: {},
      successNotification: false, errorNotification: false }, klaar);

  return (
    <div className="mt-kaart">
      <div className="mt-kop">
        <span className="mt-kop__icoon" aria-hidden="true">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
            <circle cx="9" cy="8" r="3.2" /><path d="M3 19c.6-3.2 3-5 6-5s5.4 1.8 6 5" /><circle cx="17" cy="9" r="2.6" /><path d="M16 14.2c2.4.2 4.3 1.8 4.9 4.8" />
          </svg>
        </span>
        <span className="mt-kop__tekst">
          <strong>{t("mijnTeams.titel")}</strong>
          <span>{t("mijnTeams.uitleg")}</span>
        </span>
        <StatusBadge tone="success">{t("mijnTeams.actief", { args: [String(vast.length + vandaag.length)] })}</StatusBadge>
      </div>

      {vast.length + vandaag.length === 0 ? (
        <p className="mt-leeg">{t("mijnTeams.geen")}</p>
      ) : (
        <ul className="mt-lijst">
          {vast.map((x) => (
            <li key={x.code}>
              <span className="mt-teken mt-teken--vast" aria-hidden="true"><CheckIcon width={18} height={18} /></span>
              <span className="mt-naam">{x.naam}</span>
              <StatusBadge tone="neutral">{t("mijnTeams.vast")}</StatusBadge>
            </li>
          ))}
          {vandaag.map((x) => (
            <li key={x.code} className="mt-lijst__vandaag">
              <span className="mt-teken mt-teken--vandaag" aria-hidden="true"><ClockIcon width={18} height={18} /></span>
              <span className="mt-naam">
                {x.naam}
                <small>{t("mijnTeams.totMiddernacht")}</small>
              </span>
              <StatusBadge tone="info">{t("mijnTeams.vandaag")}</StatusBadge>
              <button type="button" className="mt-weg" disabled={bezig} onClick={() => haalWeg(x.code)}
                aria-label={t("mijnTeams.loslaten", { args: [x.naam] })} title={t("mijnTeams.loslaten", { args: [x.naam] })}>
                <XIcon width={16} height={16} />
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="mt-toevoegen">
        <label htmlFor="mt-keuze">{t("mijnTeams.tijdelijk")}</label>
        {kiesbaar.length === 0 ? (
          <p className="mt-leeg">{t("mijnTeams.nietsTeKiezen")}</p>
        ) : (
          <>
            <select id="mt-keuze" className="fui-field__input" value={gekozen} onChange={(e) => setKeuze(e.target.value)}>
              {kiesbaar.map((x) => <option key={x.code} value={x.code}>{x.naam}</option>)}
            </select>
            <Button variant="primary" onClick={voegToe} disabled={bezig || !gekozen}>
              <PlusIcon width={16} height={16} /> {t("mijnTeams.voegToe")}
            </Button>
          </>
        )}
      </div>
      {fout && <p className="fawkes-error" role="alert">{fout}</p>}
    </div>
  );
}
